import { skipToken } from '@reduxjs/toolkit/query';
import { NetworkStatuses } from '@strata/constants';
import { DateFormats, formatDate, formatPhoneNumber } from '@strata/helper-functions';
import { FormSectionWithRows } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { StatusIndicator } from '@strata/react-status-indicators';
import { LiteSupplierTag } from '@strata/react-tags';
import PropTypes from 'prop-types';

import { GRANDFATHERED_SERVICING_SUPPLIER_BANNER } from 'constants/grandfatheredTreatment';
import { useShowEReferralStatus } from 'services/eReferralHelpers';
import { useGetSupplierDetailsQuery } from 'store/api/referenceApi';
import { useGetEReferralStatusQuery } from 'store/api/xComApi';

const E_REFERRAL_STATUS_CODE = {
    SENT: 'SENT',
    IN_PROGRESS: 'IN PROGRESS',
    NOT_SENT: 'NOT SENT',
};

const E_REFERRAL_STATUS_MAP = {
    [E_REFERRAL_STATUS_CODE.SENT]: StatusIndicator.Status.SUCCESS,
    [E_REFERRAL_STATUS_CODE.IN_PROGRESS]: StatusIndicator.Status.IN_PROGRESS,
    [E_REFERRAL_STATUS_CODE.NOT_SENT]: StatusIndicator.Status.DEFAULT,
};

const E_REFERRAL_STATUS_TOOLTIP_MAP = {
    [E_REFERRAL_STATUS_CODE.SENT]: 'The e-referral was sent successfully.',
    [E_REFERRAL_STATUS_CODE.IN_PROGRESS]: 'The e-referral is being processed.',
    [E_REFERRAL_STATUS_CODE.NOT_SENT]: 'The e-referral was not sent.',
};

const getMappedEReferralStatus = (eReferralStatus) => {
    const statusCode = eReferralStatus?.status_code
        ? eReferralStatus.status_code
        : E_REFERRAL_STATUS_CODE.NOT_SENT;
    return {
        message: statusCode,
        status: E_REFERRAL_STATUS_MAP[statusCode],
        tooltipText: E_REFERRAL_STATUS_TOOLTIP_MAP[statusCode],
    };
};

const buildSupplierSectionRowOne = (showGrandfatheredSupplierBanner, showEReferralStatus) => {
    if (!showGrandfatheredSupplierBanner && !showEReferralStatus) {
        return undefined;
    }
    return (
        <div className="flex__gap--medium" style={{ flexDirection: 'column', width: '100%' }}>
            {showGrandfatheredSupplierBanner && (
                <InlineMessage
                    intent={InlineMessage.Intent.INFO}
                    wrapText
                    label={GRANDFATHERED_SERVICING_SUPPLIER_BANNER}
                />
            )}
            {showEReferralStatus && (
                <InlineMessage
                    intent={InlineMessage.Intent.INFO}
                    label="The supplier is configured to receive e-referrals"
                />
            )}
        </div>
    );
};

// eslint-disable-next-line complexity
export const SupplierDetails = ({ referralDetails = undefined, referralFaxStatus = undefined }) => {
    const { data: supplierDetails, isFetching } = useGetSupplierDetailsQuery(
        referralDetails?.supplierNpi ? { supplierNpi: referralDetails?.supplierNpi } : skipToken,
    );

    const showEReferralStatus = useShowEReferralStatus(referralDetails, supplierDetails);

    const { data: eReferralStatus } = useGetEReferralStatusQuery(
        referralDetails?.referralId && showEReferralStatus
            ? {
                  referralId: referralDetails?.referralId,
              }
            : skipToken,
    );

    const mappedEReferralStatus = getMappedEReferralStatus(eReferralStatus);

    const canSendFax = supplierDetails?.fax;
    const rowOneMessages = buildSupplierSectionRowOne(
        referralDetails?.grandfathered,
        showEReferralStatus,
    );

    return (
        <FormSectionWithRows
            sectionTitle="Servicing Supplier Details"
            width="100%"
            isLoading={isFetching && referralDetails?.supplierNpi !== null}
            rowOneContent={rowOneMessages}
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Name"
                        value={
                            supplierDetails?.organizationName && (
                                <div className="flex__gap--small" style={{ alignItems: 'center' }}>
                                    {supplierDetails.organizationName}
                                    <LiteSupplierTag
                                        lite={
                                            supplierDetails.networkStatus === NetworkStatuses.LITE
                                        }
                                    />
                                </div>
                            )
                        }
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="NPI"
                        value={supplierDetails?.supplierNpi}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Phone"
                        value={formatPhoneNumber(supplierDetails?.phone)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Fax"
                        value={formatPhoneNumber(supplierDetails?.fax)}
                    />
                </>
            }
            rowFourContent={
                showEReferralStatus && (
                    <>
                        <div style={{ width: '50%' }}>
                            <DetailLabel label="Last E-Referral Status" />
                            <StatusIndicator
                                message={mappedEReferralStatus?.message}
                                status={mappedEReferralStatus?.status}
                                tooltipText={mappedEReferralStatus?.tooltipText}
                            />
                        </div>
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Last E-Referral Sent"
                            value={formatDate(eReferralStatus?.last_updated, DateFormats.DATE_TIME)}
                        />
                    </>
                )
            }
            rowFiveContent={
                <>
                    <div style={{ width: '50%' }}>
                        <DetailLabel label="Last Fax Status" />
                        {canSendFax ? (
                            <StatusIndicator
                                message={referralFaxStatus?.supplier?.message}
                                status={StatusIndicator.Status[referralFaxStatus?.supplier?.status]}
                                tooltipText={referralFaxStatus?.supplier?.tooltip}
                            />
                        ) : (
                            <StatusIndicator
                                message="CAN'T SEND"
                                status={StatusIndicator.Status.CAUTION}
                                tooltipText="Fax number doesn't exist."
                            />
                        )}
                    </div>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Last Fax Sent"
                        value={formatDate(
                            referralFaxStatus?.supplier?.date_sent,
                            DateFormats.DATE_TIME,
                        )}
                    />
                </>
            }
        />
    );
};

SupplierDetails.propTypes = {
    referralDetails: PropTypes.shape({
        supplierNpi: PropTypes.string,
        grandfathered: PropTypes.bool,
    }),
    referralFaxStatus: PropTypes.shape({
        supplier: PropTypes.shape({
            message: PropTypes.string,
            status: PropTypes.string,
            date_sent: PropTypes.string,
            tooltip: PropTypes.string,
        }),
    }),
};
